import { useMemo } from 'react';
import { styled } from '@material-ui/core';
import SButton, { SButtonProps } from '../../../../sbutton';
import { TextColor } from '../../../../../enums';
import { SearchFormOptions } from '../../enums';
import {
  DocumentSearchIcon,
  ImageIcon,
  LanguageIcon,
  SearchIcon
} from '../../../../sicons';

const StyledButton = styled(
  ({ active, ...rest }: SButtonProps & { active: boolean }) => (
    <SButton {...rest} />
  )
)(({ theme }) => ({
  padding: theme.spacing(2, 3),
  minHeight: 40,
  height: 40,
  borderRadius: 120,
  '& .MuiButton-startIcon > svg > path': {
    fill: ({ active }: { active: boolean }) =>
      active ? TextColor.Blue : TextColor.SlateGrey
  }
}));

const size = 16;

export const lables = {
  [SearchFormOptions.url]: 'URL Match',
  [SearchFormOptions.text]: 'Type & Ask',
  [SearchFormOptions.equipment]: 'Equipment Finder',
  [SearchFormOptions.image]: 'Image Match',
  [SearchFormOptions.competitor]: 'Competitor Match'
};

export const shortLables = {
  [SearchFormOptions.url]: 'By Link',
  [SearchFormOptions.text]: 'By Text',
  [SearchFormOptions.equipment]: 'By Finder',
  [SearchFormOptions.image]: 'By Image',
  [SearchFormOptions.competitor]: 'By Competitor'
};

const icons = {
  [SearchFormOptions.text]: <SearchIcon style={{ fontSize: size }} />,
  [SearchFormOptions.url]: <LanguageIcon style={{ fontSize: size }} />,
  [SearchFormOptions.image]: <ImageIcon style={{ fontSize: size }} />,
  [SearchFormOptions.competitor]: (
    <DocumentSearchIcon style={{ fontSize: size }} />
  )
};

const tooltips = {
  [SearchFormOptions.text]: `Enter product details such as name, model, specifications, or key features to find the closest match in your catalog. 
  e.g. “Zep Neutral pH Floor Cleaner restores shine and cleans floors. Ideal for vinyl, granite, marble, and stone surfaces, 
  it works with automatic scrubbers or mop and bucket systems.`,
  [SearchFormOptions.url]: `Paste a product Web Link (e.g., from Amazon or other e-commerce websites`,
  [SearchFormOptions.image]: `Upload an image and text description of a product to instantly find visually similar items in your catalog.`,
  [SearchFormOptions.competitor]: `Enter competitors information to find a suitable alternative Competitor’s`
};

interface ProductMatchButtonProps {
  type: SearchFormOptions;
  active: boolean;
  expanded?: boolean;
  shortName?: boolean;
  onClick: (type: SearchFormOptions) => void;
}

export const ProductMatchButton = ({
  type,
  active,
  expanded,
  shortName,
  onClick
}: ProductMatchButtonProps) => {
  const name = useMemo(
    () => (shortName ? shortLables[type] : lables[type]),
    [shortName, type]
  );
  const isSpec = useMemo(
    () => name === lables[SearchFormOptions?.text] && expanded,
    [name, expanded]
  );

  return (
    <StyledButton
      tooltipTitle={tooltips[type as keyof typeof tooltips]}
      variant="outlined"
      active={active}
      color={active ? 'primary' : 'default'}
      style={{
        color: active ? TextColor.Blue : TextColor.SlateGrey,
        backgroundColor: active ? '#EBF5FF' : 'transparent'
      }}
      startIcon={
        isSpec
          ? icons[SearchFormOptions.competitor]
          : icons[type as keyof typeof tooltips]
      }
      onClick={() => onClick(type)}>
      {isSpec ? 'Specs Match' : name}
    </StyledButton>
  );
};
